import { InteractionRequiredAuthError } from '@azure/msal-browser';
import msalInstance from '../auth/msalInstance';
import { loginRequest } from '../auth/authConfig';

/**
 * Acquires a Bearer token from Azure AD silently (uses cached token if valid).
 * Falls back to redirect login if interaction is required.
 */
export async function getMidpointAuthHeaders(): Promise<Record<string, string>> {
  const accounts = msalInstance.getAllAccounts();

  if (accounts.length === 0) {
    throw new Error('No authenticated user. Please log in.');
  }

  try {
    const tokenResponse = await msalInstance.acquireTokenSilent({
      ...loginRequest,
      account: accounts[0],
    });

    // Clear redirect flag on successful token acquisition
    sessionStorage.removeItem('msal_auth_redirect');
    return {
      Authorization: `Bearer ${tokenResponse.accessToken}`,
      'Content-Type': 'application/json',
      Accept: 'application/json',
    };
  } catch (error: any) {
    const isTimeout =
      error?.errorCode === 'timeout' ||
      error?.message?.includes('timed_out') ||
      error?.name === 'BrowserAuthError';

    const isInteractionRequired =
      error instanceof InteractionRequiredAuthError ||
      error?.message?.includes('interaction_required');

    if (isTimeout || isInteractionRequired) {
      // Prevent infinite redirect loops using a session flag
      const REDIRECT_FLAG = 'msal_auth_redirect';
      if (sessionStorage.getItem(REDIRECT_FLAG)) {
        // Already redirected once — clear flag and reload silently
        sessionStorage.removeItem(REDIRECT_FLAG);
        window.location.href = '/';
        return {} as Record<string, string>;
      }
      sessionStorage.setItem(REDIRECT_FLAG, 'true');
      await msalInstance.acquireTokenRedirect(loginRequest);
      return {} as Record<string, string>;
    }

    throw error;
  }
}

// ── Legacy exports kept so existing imports don't break ──────────────────────

export interface MidpointCredentials {
  username: string;
  password: string;
}

export enum MidpointAuthError {
  INVALID_CREDENTIALS = 'INVALID_CREDENTIALS',
  AUTHENTICATION_FAILED = 'AUTHENTICATION_FAILED',
  MISSING_CREDENTIALS = 'MISSING_CREDENTIALS',
  UNAUTHORIZED_ACCESS = 'UNAUTHORIZED_ACCESS',
}

export class MidpointAuthenticationError extends Error {
  constructor(
    public type: MidpointAuthError,
    message: string,
    public statusCode?: number
  ) {
    super(message);
    this.name = 'MidpointAuthenticationError';
  }
}

/** @deprecated — use getMidpointAuthHeaders() instead */
export class MidpointAuthService {
  isConfigured() { return true; }
  getAuthHeaders() { return {}; }
}

/** @deprecated — use getMidpointAuthHeaders() instead */
export function createMidpointAuthFromEnv(): MidpointAuthService {
  return new MidpointAuthService();
}
